// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useLegalForms } from '@easyflow/ui-frontend';
import { toErr } from '@easyflow/shared';
import {
  FileText, Receipt, CreditCard, Search, Plus, Trash2, Eye, Loader2, Save, X,
  ChevronDown, ChevronRight, DollarSign, CheckCircle, XCircle, Info, FileCheck
} from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterClientListRow as MasterClient,
  CrmMasterClient as MasterClientBase,
  CrmMasterInvoice as Invoice,
  CrmMasterClientFullDetail as ClientDetailData,
  CrmMasterTenantPriceList as ClientPriceList,
  CrmMasterPriceList as PriceList,
  CrmMasterPaymentAuditLogEntry as AuditLogEntry,
  CrmMasterStripeDiagnosis as StripeDiagnosis,
} from '@easyflow/shared';
import {
  Tip, pipelineColors, pipelineLabels, contractStatusBadge, contractStatusLabels, InputField,
  AddressField, SelectField, Badge, ModalWrapper
} from './shared';

// Task #3875 — campi legacy mai restituiti dalle query attuali (SELECT su
// master.crm_customers senza join): lettura difensiva, comportamento invariato.
const legacyStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const legacyNum = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

export function TabClienti() {
  const { toast } = useToast();
  const { legalForms } = useLegalForms();
  const [clients, setClients] = useState<MasterClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  // Il draft di modifica viene dal full-detail (senza i conteggi della lista).
  const [editClient, setEditClient] = useState<MasterClientBase | null>(null);
  const [saving, setSaving] = useState(false);
  const [clientPriceLists, setClientPriceLists] = useState<ClientPriceList[]>([]);
  const [allPriceLists, setAllPriceLists] = useState<PriceList[]>([]);
  const [assignPlId, setAssignPlId] = useState('');
  const [clientDetail, setClientDetail] = useState<ClientDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'overview' | 'contracts' | 'invoices' | 'payments' | 'edit'>('overview');
  const [suspendModal, setSuspendModal] = useState<{ contractId: string; contractNumber: string } | null>(null);
  const [resumeModal, setResumeModal] = useState<{ contractId: string; contractNumber: string } | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [resumeReason, setResumeReason] = useState('');
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLogContract, setAuditLogContract] = useState<string | null>(null);
  const [suspendTenantLoading, setSuspendTenantLoading] = useState(false);
  const [stripeDiagnosis, setStripeDiagnosis] = useState<StripeDiagnosis | null>(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [chargingInstallment, setChargingInstallment] = useState<string | null>(null);
  const [manualPayModal, setManualPayModal] = useState<{ id: string; number: number; amount: number } | null>(null);
  const [manualPayMethod, setManualPayMethod] = useState<'bank_transfer' | 'cash' | 'cheque'>('bank_transfer');
  const [manualPayRef, setManualPayRef] = useState('');
  const [manualPayNotes, setManualPayNotes] = useState('');
  const [manualPayLoading, setManualPayLoading] = useState(false);
  const [expandedQuotes, setExpandedQuotes] = useState<Set<string>>(new Set());
  const [deleteModal, setDeleteModal] = useState<{ id: string; name: string } | null>(null);
  const [deleteMode, setDeleteMode] = useState<'client_only' | 'cascade_safe' | 'cascade_surgical'>('client_only');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadClientPriceLists = useCallback(async (clientId: string) => {
    try {
      const [plRes, allPlRes] = await Promise.all([
        api.get(`${CRM_BASE}/clients/${clientId}/price-lists`),
        api.get<PriceList[]>(`${CRM_BASE}/price-lists`),
      ]);
      if (plRes.success && plRes.data) setClientPriceLists(Array.isArray(plRes.data) ? plRes.data : []);
      if (allPlRes.success && allPlRes.data) setAllPriceLists(Array.isArray(allPlRes.data) ? allPlRes.data : []);
    } catch {}
  }, []);

  const handleAssignPl = async () => {
    if (!editClient || !assignPlId) return;
    await api.post(`${CRM_BASE}/clients/${editClient.id}/price-lists`, { price_list_id: assignPlId });
    setAssignPlId('');
    void loadClientPriceLists(editClient.id);
  };

  const handleRemovePl = async (plId: string) => {
    if (!editClient) return;
    await api.delete(`${CRM_BASE}/clients/${editClient.id}/price-lists/${plId}`);
    void loadClientPriceLists(editClient.id);
  };

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('stage_slug', statusFilter);
      const res = await api.get<MasterClient[] | { clients?: MasterClient[] }>(`${CRM_BASE}/clients?${params.toString()}`);
      if (res.success && res.data) setClients(Array.isArray(res.data) ? res.data : (res.data).clients || []);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { void loadClients(); }, [loadClients]);

  const openClientDetail = async (clientId: string) => {
    if (detailLoading) return;
    setDetailLoading(true);
    setDetailTab('overview');
    try {
      const res = await api.get<ClientDetailData>(`${CRM_BASE}/clients/${clientId}/full-detail`);
      if (res.success && res.data) {
        setClientDetail(res.data);
        if (res.data.client) setEditClient({ ...res.data.client });
        setExpandedQuotes(new Set());
        void loadClientPriceLists(clientId);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveClient = async () => {
    if (!editClient) return;
    setSaving(true);
    try {
      await api.put(`${CRM_BASE}/clients/${editClient.id}`, editClient);
      void loadClients();
      void openClientDetail(editClient.id);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (id: string, name: string) => {
    setDeleteModal({ id, name });
    setDeleteMode('client_only');
    setDeleteConfirmText('');
  };

  const handleDeleteClient = async () => {
    if (!deleteModal || deleteConfirmText.trim().toUpperCase() !== 'ELIMINA') return;
    setDeleteLoading(true);
    try {
      const res = await api.delete(`${CRM_BASE}/clients/${deleteModal.id}?mode=${deleteMode}`);
      if (res.success) {
        setDeleteModal(null);
        setDeleteConfirmText('');
        if (clientDetail?.client?.id === deleteModal.id) { setClientDetail(null); setEditClient(null); }
        void loadClients();
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSuspendPayments = async () => {
    if (!suspendModal || !suspendReason.trim()) return;
    setSuspendLoading(true);
    try {
      await api.post(`${CRM_BASE}/contracts/${suspendModal.contractId}/suspend-payments`, { reason: suspendReason.trim(), actor: 'admin' });
      setSuspendModal(null);
      setSuspendReason('');
      if (clientDetail?.client?.id) void openClientDetail(clientDetail.client.id);
    } catch (e: unknown) {
      const __e = toErr(e);
      alert(__e.message || 'Errore sospensione');
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleResumePayments = async () => {
    if (!resumeModal || !resumeReason.trim()) return;
    setResumeLoading(true);
    try {
      const res = await api.post<{ daysSuspended?: number }>(`${CRM_BASE}/contracts/${resumeModal.contractId}/resume-payments`, { actor: 'admin', reason: resumeReason.trim() });
      if (res.data?.daysSuspended) {
        alert(`Pagamenti ripresi. Rate slittate di ${res.data.daysSuspended} giorni.`);
      }
      setResumeModal(null);
      setResumeReason('');
      if (clientDetail?.client?.id) void openClientDetail(clientDetail.client.id);
    } catch (e: unknown) {
      const __e = toErr(e);
      alert(__e.message || 'Errore ripresa');
    } finally {
      setResumeLoading(false);
    }
  };

  const loadAuditLog = async (contractId: string) => {
    try {
      const res = await api.get<AuditLogEntry[]>(`${CRM_BASE}/contracts/${contractId}/payment-audit-log`);
      if (res.success) {
        setAuditLog(res.data || []);
        setAuditLogContract(contractId);
      }
    } catch {}
  };

  const handleSuspendTenant = async (tenantId: string) => {
    if (!confirm('ATTENZIONE: Stai per sospendere il tenant. L\'accesso sarà bloccato per tutti gli utenti. Continuare?')) return;
    setSuspendTenantLoading(true);
    try {
      await api.post(`${CRM_BASE}/tenants/${tenantId}/suspend-from-payment`, { actor: 'admin' });
      alert('Tenant sospeso con successo.');
      if (clientDetail?.client?.id) void openClientDetail(clientDetail.client.id);
    } catch (e: unknown) {
      const __e = toErr(e);
      alert(__e.message || 'Errore sospensione tenant');
    } finally {
      setSuspendTenantLoading(false);
    }
  };

  const runStripeDiagnosis = async (pipelineEntryId: string) => {
    setDiagnosisLoading(true);
    setStripeDiagnosis(null);
    try {
      const res = await api.get<StripeDiagnosis>(`${CRM_BASE}/payments/diagnose/${pipelineEntryId}`);
      if (res.success && res.data) setStripeDiagnosis(res.data);
      else toast({ title: 'Errore', description: 'Impossibile ottenere la diagnosi', variant: 'destructive' });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore diagnosi', description: __e.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setDiagnosisLoading(false);
    }
  };

  const handleManualPayment = async () => {
    if (!manualPayModal) return;
    setManualPayLoading(true);
    try {
      const res = await api.post<{ message?: string; warnings?: string[] }>(`${CRM_BASE}/installments/${manualPayModal.id}/mark-paid-manual`, {
        payment_method: manualPayMethod,
        reference: manualPayRef.trim() || undefined,
        notes: manualPayNotes.trim() || undefined,
      });
      if (res.success) {
        const desc = res.data?.warnings?.length
          ? `${res.data.message}. Avvisi: ${res.data.warnings.join('; ')}`
          : res.data?.message || 'Pagamento manuale registrato con successo';
        toast({ title: 'Pagamento registrato', description: desc, variant: res.data?.warnings?.length ? 'destructive' : undefined });
        setManualPayModal(null);
        setManualPayMethod('bank_transfer');
        setManualPayRef('');
        setManualPayNotes('');
        if (clientDetail?.client?.id) void openClientDetail(clientDetail.client.id);
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore sconosciuto', variant: 'destructive' });
      }
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore pagamento manuale', description: (__e?.response?.data as { error?: string } | undefined)?.error || __e.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setManualPayLoading(false);
    }
  };

  const chargeInstallmentManually = async (installmentId: string) => {
    if (!confirm('Confermi l\'addebito manuale immediato di questa rata? L\'importo verrà addebitato sul metodo di pagamento del cliente.')) return;
    setChargingInstallment(installmentId);
    try {
      const res = await api.post<{ message?: string }>(`${CRM_BASE}/payments/charge-installment/${installmentId}`);
      if (res.success) {
        toast({ title: 'Addebito avviato', description: res.data?.message || 'Addebito in corso' });
        if (clientDetail?.client?.id) void openClientDetail(clientDetail.client.id);
      } else {
        toast({ title: 'Errore addebito', description: res.error || 'Errore sconosciuto', variant: 'destructive' });
      }
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore addebito', description: (__e?.response?.data as { error?: string } | undefined)?.error || __e.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setChargingInstallment(null);
    }
  };

  const updateClient = (field: string) => (value: string) => setEditClient((c) => ({ ...(c as MasterClient), [field]: value }));

  const sectors = [...new Set(clients.map(c => c.sector).filter((s): s is string => Boolean(s)))];

  const filteredClients = clients.filter(c => {
    if (sectorFilter && c.sector !== sectorFilter) return false;
    return true;
  });

  const quoteStatusLabel: Record<string, string> = { draft: 'Bozza', sent: 'Inviato', accepted: 'Accettato', rejected: 'Rifiutato', payment_setup_sent: 'Setup Pagamento', payment_confirmed: 'Pagamento Confermato', expired: 'Scaduto' };
  const instStatusLabel: Record<string, string> = { pending: 'In Attesa', paid: 'Pagata', overdue: 'Scaduta', failed: 'Fallita', processing: 'In Elaborazione', planned: 'Pianificata' };
  const invStatusLabel: Record<string, string> = { draft: 'Da Emettere', issued: 'Emessa', sent: 'Inviata', paid: 'Pagata', overdue: 'Scaduta', cancelled: 'Annullata', credited: 'Stornata', suspended: 'Sospesa' };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-muted-foreground/70" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, P.IVA, email..."
            className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg pl-10 pr-3 py-2 text-sm" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
          <option value="">Tutti gli stati</option>
          {Object.entries(pipelineLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {sectors.length > 0 && (
          <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)}
            className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
            <option value="">Tutti i settori</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">{filteredClients.length} clienti</span>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border dark:border-gray-700">
              <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Nome</th>
              <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">P.IVA</th>
              <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Stato Pipeline</th>
              <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Referente</th>
              <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Email</th>
              <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Settore</th>
              <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Caricamento...</td></tr>
            ) : filteredClients.length === 0 ? (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Nessun cliente trovato</td></tr>
            ) : filteredClients.map((c) => (
              <tr key={c.id} onClick={() => openClientDetail(c.id)}
                className="border-b border-border dark:border-gray-700/50 hover:bg-muted dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
                <td className="py-3 px-4 text-foreground dark:text-white font-medium">{c.company_name || c.contact_name || legacyStr(c.tenant_name) || '-'}</td>
                <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 font-mono text-sm">{c.vat_number || '-'}</td>
                <td className="py-3 px-4">
                  <Badge className={pipelineColors[legacyStr(c.stage_slug) ?? ''] || 'bg-muted/500/20 text-muted-foreground/70'}>
                    {pipelineLabels[legacyStr(c.stage_slug) ?? ''] || legacyStr(c.stage_slug) || 'N/A'}
                  </Badge>
                </td>
                <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{[c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || '-'}</td>
                <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{c.contact_email || '-'}</td>
                <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{c.sector || '-'}</td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Tip label="Dettaglio"><button onClick={(e) => { e.stopPropagation(); void openClientDetail(c.id); }} className="text-blue-400 hover:text-blue-300 p-1"><Eye className="h-4 w-4" /></button></Tip>
                    <Tip label="Elimina"><button onClick={(e) => { e.stopPropagation(); openDeleteModal(c.id, c.company_name || c.contact_name || legacyStr(c.tenant_name) || 'Cliente'); }} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button></Tip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ModalWrapper open={!!clientDetail || detailLoading} onClose={() => { setClientDetail(null); setEditClient(null); }} title={clientDetail?.client ? `${clientDetail.client.company_name || clientDetail.client.contact_name || 'Cliente'}` : 'Caricamento...'} wide>
        {detailLoading ? (
          <div className="text-muted-foreground dark:text-muted-foreground/70 text-center py-12">Caricamento dettaglio cliente...</div>
        ) : clientDetail && (
          <div className="space-y-4">
            <div className="flex gap-1 border-b border-border dark:border-gray-700 overflow-x-auto">
              {[
                { key: 'overview', label: 'Panoramica', icon: '📊' },
                { key: 'contracts', label: `Contratti (${clientDetail.contracts?.length || 0})`, icon: '📄' },
                { key: 'invoices', label: `Fatture (${clientDetail.invoices?.length || 0})`, icon: '🧾' },
                { key: 'payments', label: `Rate (${clientDetail.installments?.length || 0})`, icon: '💳' },
                { key: 'edit', label: 'Anagrafica', icon: '✏️' },
              ].map(t => (
                <button key={t.key} onClick={() => setDetailTab(t.key as 'overview' | 'contracts' | 'invoices' | 'payments' | 'edit')}
                  className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${detailTab === t.key ? 'border-blue-500 text-blue-400' : 'border-transparent text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white'}`}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {detailTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Preventivi', value: clientDetail.summary?.total_quotes || 0, color: 'text-blue-400', icon: FileText },
                    { label: 'Contratti Attivi', value: clientDetail.summary?.active_contracts || 0, color: 'text-green-400', icon: FileCheck },
                    { label: 'Fatture', value: clientDetail.summary?.total_invoices || 0, color: 'text-purple-400', icon: Receipt },
                    { label: 'Rate Totali', value: clientDetail.summary?.total_installments || 0, color: 'text-amber-400', icon: CreditCard },
                  ].map((kpi, i) => (
                    <div key={i} className="bg-muted/50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
                        <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">{kpi.label}</span>
                      </div>
                      <p className="text-xl font-bold text-foreground dark:text-white">{kpi.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <p className="text-green-600 dark:text-green-400 text-xs mb-1">Pagato</p>
                    <p className="text-lg font-bold text-green-700 dark:text-green-300">€ {Number(clientDetail.summary?.amount_paid || 0).toFixed(2)}</p>
                    <p className="text-green-600 dark:text-green-400/60 text-xs">{clientDetail.summary?.installments_paid || 0} rate</p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-amber-600 dark:text-amber-400 text-xs mb-1">Da Incassare</p>
                    <p className="text-lg font-bold text-amber-700 dark:text-amber-300">€ {Number(clientDetail.summary?.amount_pending || 0).toFixed(2)}</p>
                    <p className="text-amber-600 dark:text-amber-400/60 text-xs">{clientDetail.summary?.installments_pending || 0} rate</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-red-600 dark:text-red-400 text-xs mb-1">Scaduto</p>
                    <p className="text-lg font-bold text-red-700 dark:text-red-300">€ {Number(clientDetail.summary?.amount_overdue || 0).toFixed(2)}</p>
                    <p className="text-red-600 dark:text-red-400/60 text-xs">{clientDetail.summary?.installments_overdue || 0} rate</p>
                  </div>
                </div>

                {clientDetail.quotes?.length > 0 && (
                  <div>
                    <h4 className="text-foreground dark:text-white font-medium mb-2 flex items-center gap-2"><FileText className="h-4 w-4" /> Prodotti / Preventivi</h4>
                    <div className="space-y-2">
                      {clientDetail.quotes.map((q) => {
                        const items = Array.isArray(q.items) ? q.items : [];
                        const itemsCount = items.length;
                        const isExpanded = expandedQuotes.has(q.id);
                        const toggle = () => {
                          setExpandedQuotes(prev => {
                            const next = new Set(prev);
                            if (next.has(q.id)) next.delete(q.id); else next.add(q.id);
                            return next;
                          });
                        };
                        return (
                          <div key={q.id} className="bg-muted/50 dark:bg-gray-700/50 rounded-lg">
                            <button
                              type="button"
                              onClick={toggle}
                              className="w-full flex items-center justify-between p-3 cursor-pointer hover:bg-muted dark:hover:bg-gray-700 rounded-lg transition-colors text-left"
                              aria-expanded={isExpanded}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {isExpanded
                                  ? <ChevronDown className="h-4 w-4 text-muted-foreground dark:text-muted-foreground/70 shrink-0" />
                                  : <ChevronRight className="h-4 w-4 text-muted-foreground dark:text-muted-foreground/70 shrink-0" />}
                                <span className="text-foreground dark:text-white font-medium text-sm truncate">{q.quote_number}</span>
                                <Badge className={q.status === 'accepted' || q.status === 'payment_confirmed' ? 'bg-green-500/20 text-green-400' : q.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-muted/500/20 text-muted-foreground/70'}>
                                  {quoteStatusLabel[q.status ?? ''] || q.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <span className="text-xs text-muted-foreground dark:text-muted-foreground/70">{itemsCount} {itemsCount === 1 ? 'articolo' : 'articoli'}</span>
                                <span className="text-foreground dark:text-white font-semibold text-sm">€ {Number(q.total_gross || '0').toFixed(2)}</span>
                              </div>
                            </button>
                            {itemsCount > 0 && (
                              <div
                                className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                                aria-hidden={!isExpanded}
                              >
                                <div className="overflow-hidden">
                                  <div className="px-3 pb-3 space-y-0.5 border-t border-border dark:border-gray-700/50">
                                    {items.map((item, idx: number) => (
                                      <div key={idx} className="flex items-center justify-between text-xs text-muted-foreground dark:text-muted-foreground/70 pt-1">
                                        <span>{item.product_name}{item.description ? ` - ${item.description}` : ''}</span>
                                        <span>x{item.quantity} @ € {Number(item.unit_price || 0).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {clientDetail.payment_methods?.length > 0 && (
                  <div>
                    <h4 className="text-foreground dark:text-white font-medium mb-2 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Metodi di Pagamento</h4>
                    <div className="space-y-1">
                      {clientDetail.payment_methods.map((pm) => (
                        <div key={pm.id} className="flex items-center justify-between bg-muted/50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-foreground dark:text-white text-sm font-mono">
                              {pm.stripe_pm_brand ? pm.stripe_pm_brand.toUpperCase() : pm.stripe_pm_type} **** {pm.stripe_pm_last4}
                            </span>
                            {pm.stripe_pm_exp_month && <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">scade {String(pm.stripe_pm_exp_month).padStart(2, '0')}/{pm.stripe_pm_exp_year}</span>}
                            {pm.is_default && <Badge className="bg-blue-500/20 text-blue-400">Predefinito</Badge>}
                            {pm.quote_id && <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs ml-1">Prev. #{clientDetail.quotes?.find((q) => q.id === pm.quote_id)?.quote_number || pm.quote_id.slice(0, 8)}</span>}
                          </div>
                          {!pm.is_default && (
                            <button onClick={async () => {
                              const clientId = clientDetail.client?.id;
                              if (!clientId) return;
                              if (!confirm('Eliminare questo metodo di pagamento? Verrà rimosso anche da Stripe.')) return;
                              try {
                                await api.delete(`${CRM_BASE}/clients/${clientId}/payment-methods/${pm.id}`);
                                void openClientDetail(clientId);
                                toast({ title: 'Metodo eliminato', description: 'Il metodo di pagamento è stato rimosso.' });
                              } catch (err: unknown) {
                                const __err = toErr(err);
                                toast({ title: 'Errore', description: (__err?.response?.data as { error?: string } | undefined)?.error || 'Impossibile eliminare', variant: 'destructive' });
                              }
                            }} className="text-muted-foreground/70 hover:text-red-400 p-1">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-foreground dark:text-white font-medium mb-2">Dati Aziendali</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    {[
                      { l: 'Ragione Sociale', v: clientDetail.client?.company_name },
                      { l: 'Forma Giuridica', v: clientDetail.client?.legal_form },
                      { l: 'P.IVA', v: clientDetail.client?.vat_number },
                      { l: 'C.F.', v: clientDetail.client?.fiscal_code },
                      { l: 'SDI', v: clientDetail.client?.sdi_code },
                      { l: 'PEC', v: clientDetail.client?.pec },
                      { l: 'Indirizzo', v: [clientDetail.client?.legal_address, clientDetail.client?.legal_city, clientDetail.client?.legal_province].filter(Boolean).join(', ') },
                      { l: 'Referente', v: [clientDetail.client?.contact_first_name, clientDetail.client?.contact_last_name].filter(Boolean).join(' ') },
                      { l: 'Email', v: clientDetail.client?.contact_email },
                      { l: 'Telefono', v: clientDetail.client?.contact_phone },
                    ].map((f, i) => (
                      <div key={i} className="bg-muted/50 dark:bg-gray-700/50 rounded px-2 py-1.5">
                        <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">{f.l}</span>
                        <p className="text-foreground dark:text-white truncate">{f.v || '-'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {detailTab === 'contracts' && (
              <div className="space-y-3">
                {((clientDetail.summary?.installments_overdue ?? 0) > 0 || clientDetail.contracts?.some((c) => c.payment_suspended)) && !!legacyStr(clientDetail.client?.tenant_id) && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-red-700 dark:text-red-300 font-medium text-sm">
                        {(clientDetail.summary?.installments_overdue ?? 0) > 0
                          ? `${clientDetail.summary?.installments_overdue ?? 0} rate scadute (€ ${Number(clientDetail.summary?.amount_overdue || 0).toFixed(2)})`
                          : 'Contratti con pagamenti sospesi'}
                      </p>
                      <p className="text-red-600 dark:text-red-400 text-xs">Valuta la sospensione del tenant se la situazione persiste.</p>
                    </div>
                    <button onClick={() => { const tid = legacyStr(clientDetail.client?.tenant_id); if (tid) void handleSuspendTenant(tid); }} disabled={suspendTenantLoading}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg disabled:opacity-50">
                      {suspendTenantLoading ? 'Sospensione...' : 'Sospendi Tenant'}
                    </button>
                  </div>
                )}
                {clientDetail.contracts?.length === 0 ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-center py-6">Nessun contratto</p>
                ) : clientDetail.contracts?.map((c) => (
                  <div key={c.id} className="bg-muted/50 dark:bg-gray-700/50 border border-border dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-foreground dark:text-white font-semibold">{c.contract_number}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={contractStatusBadge[c.status ?? ''] || 'bg-muted/500/20 text-muted-foreground/70'}>{contractStatusLabels[c.status ?? ''] || c.status}</Badge>
                        {c.payment_suspended && <Badge className="bg-amber-500/20 text-amber-400">Pagamenti Sospesi</Badge>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Preventivo</span><p className="text-foreground dark:text-white">{c.quote_number || '-'}</p></div>
                      <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Totale</span><p className="text-foreground dark:text-white">€ {Number(c.quote_total || 0).toFixed(2)}</p></div>
                      <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Firma</span><p className="text-foreground dark:text-white">{c.signed_at ? new Date(c.signed_at).toLocaleDateString('it-IT') : '-'}</p></div>
                      <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Validità</span><p className="text-foreground dark:text-white">{c.start_date ? new Date(c.start_date).toLocaleDateString('it-IT') : '-'} → {c.end_date ? new Date(c.end_date).toLocaleDateString('it-IT') : '-'}</p></div>
                    </div>
                    {c.payment_suspended && c.payment_suspended_at && (
                      <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2 text-sm">
                        <p className="text-amber-700 dark:text-amber-300 font-medium">Sospesi dal {new Date(c.payment_suspended_at).toLocaleDateString('it-IT')}</p>
                        {c.payment_suspension_reason && <p className="text-amber-600 dark:text-amber-400 text-xs mt-0.5">Motivo: {c.payment_suspension_reason}</p>}
                        {c.payment_suspended_by && <p className="text-amber-600 dark:text-amber-400 text-xs">Operatore: {c.payment_suspended_by}</p>}
                      </div>
                    )}
                    {c.contract_pipeline_status && (
                      <div className="mt-2">
                        <Badge className="bg-blue-500/20 text-blue-400">Pipeline: {c.contract_pipeline_status}</Badge>
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {!c.payment_suspended ? (
                        <button onClick={(e) => { e.stopPropagation(); setSuspendModal({ contractId: c.id, contractNumber: c.contract_number ?? '' }); }}
                          className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg">
                          Sospendi Pagamenti
                        </button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); setResumeModal({ contractId: c.id, contractNumber: c.contract_number ?? '' }); }}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg">
                          Riprendi Pagamenti
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); void loadAuditLog(c.id); }}
                        className="px-3 py-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-muted/500 text-foreground dark:text-foreground text-xs font-medium rounded-lg">
                        Storico Sospensioni
                      </button>
                    </div>
                    {auditLogContract === c.id && auditLog.length > 0 && (
                      <div className="mt-3 border-t border-border dark:border-gray-600 pt-3">
                        <h5 className="text-foreground dark:text-white text-sm font-medium mb-2">Storico Sospensioni/Riprese</h5>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {auditLog.map((log) => (
                            <div key={log.id} className={`flex items-start gap-2 text-xs p-2 rounded ${log.action === 'suspend' ? 'bg-amber-50 dark:bg-amber-900/10' : log.action === 'resume' ? 'bg-green-50 dark:bg-green-900/10' : 'bg-blue-50 dark:bg-blue-900/10'}`}>
                              <span className={`font-bold mt-0.5 ${log.action === 'suspend' ? 'text-amber-600 dark:text-amber-400' : log.action === 'resume' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                {log.action === 'suspend' ? '⏸' : log.action === 'resume' ? '▶' : '⚠️'}
                              </span>
                              <div className="flex-1">
                                <p className="text-foreground dark:text-white font-medium">
                                  {log.action === 'suspend' ? 'Pagamenti sospesi' : log.action === 'resume' ? 'Pagamenti ripresi' : log.action === 'overdue_notification' ? 'Notifica rate scadute' : log.action}
                                </p>
                                {log.reason && <p className="text-muted-foreground dark:text-muted-foreground/70">Motivo: {log.reason}</p>}
                                {log.actor && <p className="text-muted-foreground dark:text-muted-foreground/70">Operatore: {log.actor}</p>}
                                {log.details?.days_suspended && <p className="text-muted-foreground dark:text-muted-foreground/70">Giorni sospesi: {log.details.days_suspended}</p>}
                                <p className="text-muted-foreground/70 dark:text-muted-foreground">{new Date(log.created_at ?? '').toLocaleString('it-IT')}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {auditLogContract === c.id && auditLog.length === 0 && (
                      <p className="mt-2 text-muted-foreground dark:text-muted-foreground/70 text-xs italic">Nessuno storico disponibile per questo contratto.</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {suspendModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setSuspendModal(null)}>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                  <h3 className="text-foreground dark:text-white font-semibold text-lg mb-1">Sospendi Pagamenti</h3>
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-4">Contratto: {suspendModal.contractNumber}</p>
                  <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Motivo della sospensione *</label>
                  <textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)} rows={3} placeholder="Inserisci il motivo della sospensione..."
                    className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-4" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setSuspendModal(null); setSuspendReason(''); }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-foreground dark:text-foreground rounded-lg text-sm">Annulla</button>
                    <button onClick={handleSuspendPayments} disabled={!suspendReason.trim() || suspendLoading}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {suspendLoading ? 'Sospensione...' : 'Conferma Sospensione'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {resumeModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setResumeModal(null)}>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                  <h3 className="text-foreground dark:text-white font-semibold text-lg mb-1">Riprendi Pagamenti</h3>
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-4">Contratto: {resumeModal.contractNumber}</p>
                  <p className="text-muted-foreground dark:text-muted-foreground/60 text-xs mb-3">Le rate in attesa e scadute verranno riscadenzate automaticamente in base ai giorni di sospensione.</p>
                  <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Motivo della ripresa *</label>
                  <textarea value={resumeReason} onChange={e => setResumeReason(e.target.value)} rows={3} placeholder="Inserisci il motivo della ripresa..."
                    className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-4" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setResumeModal(null); setResumeReason(''); }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-foreground dark:text-foreground rounded-lg text-sm">Annulla</button>
                    <button onClick={handleResumePayments} disabled={!resumeReason.trim() || resumeLoading}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {resumeLoading ? 'Ripresa...' : 'Conferma Ripresa'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {manualPayModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setManualPayModal(null)}>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                  <h3 className="text-foreground dark:text-white font-semibold text-lg mb-1">Registra Pagamento Manuale</h3>
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm mb-4">Rata #{manualPayModal.number} — € {manualPayModal.amount.toFixed(2)}</p>
                  <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Metodo di pagamento *</label>
                  <select value={manualPayMethod} onChange={e => setManualPayMethod(e.target.value as 'bank_transfer' | 'cash' | 'cheque')}
                    className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-3">
                    <option value="bank_transfer">Bonifico bancario</option>
                    <option value="cash">Contanti</option>
                    <option value="cheque">Assegno</option>
                  </select>
                  <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Riferimento bancario (CRO / IBAN mittente)</label>
                  <input type="text" value={manualPayRef} onChange={e => setManualPayRef(e.target.value)} placeholder="Es. CRO 1234567890 o IBAN IT..."
                    className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-3" />
                  <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Note</label>
                  <textarea value={manualPayNotes} onChange={e => setManualPayNotes(e.target.value)} rows={2} placeholder="Note opzionali..."
                    className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm mb-4" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setManualPayModal(null); setManualPayMethod('bank_transfer'); setManualPayRef(''); setManualPayNotes(''); }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-foreground dark:text-foreground rounded-lg text-sm">Annulla</button>
                    <button onClick={handleManualPayment} disabled={manualPayLoading}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {manualPayLoading ? 'Registrazione...' : 'Conferma Pagamento'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {detailTab === 'invoices' && (
              <div className="space-y-3">
                {clientDetail.invoices?.length === 0 ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-center py-6">Nessuna fattura</p>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border dark:border-gray-700">
                        <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Numero</th>
                        <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Tipo</th>
                        <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Imponibile</th>
                        <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">IVA</th>
                        <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Totale</th>
                        <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Data</th>
                        <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Stato</th>
                        <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">FiC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientDetail.invoices?.map((inv) => (
                        <tr key={inv.id} className="border-b border-border dark:border-gray-700/50">
                          <td className="py-2 px-3 font-mono text-sm">
                            {legacyStr(inv.fic_number) ? (
                              <div className="flex flex-col leading-tight">
                                <span className="text-foreground dark:text-white">{legacyStr(inv.fic_number)}</span>
                                <span className="text-[10px] text-muted-foreground/70 dark:text-muted-foreground">{inv.invoice_number}</span>
                              </div>
                            ) : (
                              <span className="text-foreground dark:text-white">{inv.invoice_number}</span>
                            )}
                          </td>
                          <td className="py-2 px-3"><Badge className={inv.type === 'credit_note' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}>{inv.type === 'credit_note' ? 'Nota Credito' : 'Fattura'}</Badge></td>
                          <td className="py-2 px-3 text-right text-muted-foreground dark:text-muted-foreground/60 text-sm">€ {Number(inv.subtotal_net || 0).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground dark:text-muted-foreground/60 text-sm">€ {Number(inv.total_vat || 0).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right text-foreground dark:text-white font-medium text-sm">€ {Number(inv.total_gross || '0').toFixed(2)}</td>
                          <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('it-IT') : '-'}</td>
                          <td className="py-2 px-3"><Badge className={inv.status === 'paid' ? 'bg-green-500/20 text-green-400' : inv.status === 'draft' ? 'bg-amber-500/20 text-amber-400' : inv.status === 'suspended' ? 'bg-orange-500/20 text-orange-400' : 'bg-muted/500/20 text-muted-foreground/70'}>{invStatusLabel[inv.status ?? ''] || inv.status}</Badge></td>
                          <td className="py-2 px-3">
                            {inv.fic_document_id ? (
                              <div className="flex items-center gap-1">
                                <Badge className="bg-green-500/20 text-green-400">Sync</Badge>
                                <Tip label="XML"><a href={`/api${CRM_BASE}/fic/documents/${inv.fic_document_id}/xml`} target="_blank" rel="noreferrer"
                                  className="text-blue-400 hover:text-blue-300 text-xs font-mono px-1">XML</a></Tip>
                                <Tip label="PDF"><a href={`/api${CRM_BASE}/fic/documents/${inv.fic_document_id}/pdf`} target="_blank" rel="noreferrer"
                                  className="text-green-400 hover:text-green-300 text-xs font-mono px-1">PDF</a></Tip>
                              </div>
                            ) : <Badge className="bg-muted/500/20 text-muted-foreground/70">Locale</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {detailTab === 'payments' && (
              <div className="space-y-3">
                {clientDetail.installments?.length === 0 ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-center py-6">Nessuna rata</p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                        <p className="text-green-700 dark:text-green-300 font-bold">€ {Number(clientDetail.summary?.amount_paid || 0).toFixed(2)}</p>
                        <p className="text-green-600 dark:text-green-400 text-xs">{clientDetail.summary?.installments_paid || 0} pagate</p>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                        <p className="text-amber-700 dark:text-amber-300 font-bold">€ {Number(clientDetail.summary?.amount_pending || 0).toFixed(2)}</p>
                        <p className="text-amber-600 dark:text-amber-400 text-xs">{clientDetail.summary?.installments_pending || 0} in attesa</p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                        <p className="text-red-700 dark:text-red-300 font-bold">€ {Number(clientDetail.summary?.amount_overdue || 0).toFixed(2)}</p>
                        <p className="text-red-600 dark:text-red-400 text-xs">{clientDetail.summary?.installments_overdue || 0} scadute</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button onClick={() => { const cid = clientDetail.client?.id; if (cid) void runStripeDiagnosis(cid); }} disabled={diagnosisLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg disabled:opacity-50">
                        {diagnosisLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                        Diagnosi Stripe
                      </button>
                      {stripeDiagnosis && (
                        <button onClick={() => setStripeDiagnosis(null)} className="text-muted-foreground/70 hover:text-muted-foreground/60 text-xs">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    {stripeDiagnosis && (
                      <div className="bg-muted/50 dark:bg-gray-800 border border-border dark:border-gray-700 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-foreground dark:text-white font-semibold text-sm flex items-center gap-2">
                            <Info className="h-4 w-4 text-blue-400" />
                            Diagnosi Stripe — {stripeDiagnosis.client_name}
                          </h4>
                          <Badge className={stripeDiagnosis.issues?.length === 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {stripeDiagnosis.verdict}
                          </Badge>
                        </div>

                        {stripeDiagnosis.ok?.length > 0 && (
                          <div className="space-y-1">
                            {stripeDiagnosis.ok.map((msg: string, i: number) => (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <CheckCircle className="h-3.5 w-3.5 text-green-400 mt-0.5 shrink-0" />
                                <span className="text-green-700 dark:text-green-300">{msg}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {stripeDiagnosis.issues?.length > 0 && (
                          <div className="space-y-1">
                            {stripeDiagnosis.issues.map((msg: string, i: number) => (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <XCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                                <span className="text-red-700 dark:text-red-300">{msg}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {stripeDiagnosis.payment_methods_on_stripe && (
                          <div className="border-t border-border dark:border-gray-700 pt-2">
                            <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium mb-1">Metodi di pagamento su Stripe:</p>
                            <div className="space-y-1">
                              {stripeDiagnosis.payment_methods_on_stripe.cards?.map((c) => (
                                <div key={c.id} className="text-xs text-muted-foreground dark:text-muted-foreground/60 font-mono">
                                  Carta {c.brand?.toUpperCase()} ****{c.last4} scade {c.exp} — <span className="text-muted-foreground/70">{c.id}</span>
                                </div>
                              ))}
                              {stripeDiagnosis.payment_methods_on_stripe.sepa?.map((s) => (
                                <div key={s.id} className="text-xs text-muted-foreground dark:text-muted-foreground/60 font-mono">
                                  SEPA ****{s.last4} banca {s.bank} — <span className="text-muted-foreground/70">{s.id}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(stripeDiagnosis.due_installments?.length ?? 0) > 0 && (
                          <div className="border-t border-border dark:border-gray-700 pt-2">
                            <p className="text-red-600 dark:text-red-400 text-xs font-medium mb-1">Rate scadute da addebitare:</p>
                            <div className="space-y-1">
                              {stripeDiagnosis.due_installments?.map((di) => (
                                <div key={di.id} className="flex items-center justify-between bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                                  <span className="text-xs text-red-700 dark:text-red-300">
                                    #{di.installment_number}{di.is_entry_fee ? ' (Fee)' : ''} — € {Number(di.amount).toFixed(2)} — scaduta {new Date(di.due_date ?? '').toLocaleDateString('it-IT')}
                                  </span>
                                  <button onClick={() => chargeInstallmentManually(di.id)} disabled={chargingInstallment === di.id}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded disabled:opacity-50">
                                    {chargingInstallment === di.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
                                    Addebita Ora
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(stripeDiagnosis.recent_cron_runs?.length ?? 0) > 0 && (
                          <div className="border-t border-border dark:border-gray-700 pt-2">
                            <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium mb-1">Ultimi run cron addebiti:</p>
                            <div className="space-y-1">
                              {stripeDiagnosis.recent_cron_runs?.map((cr, i: number) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <Badge className={cr.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{cr.status}</Badge>
                                  <span className="text-muted-foreground dark:text-muted-foreground/60">{new Date(cr.started_at ?? '').toLocaleString('it-IT')}</span>
                                  <span className="text-muted-foreground/70">OK: {cr.success_count} / Err: {cr.error_count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">#</th>
                          <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Contratto</th>
                          <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Importo</th>
                          <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Scadenza</th>
                          <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Stato</th>
                          <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Pagato il</th>
                          <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-sm">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientDetail.installments?.map((inst) => (
                          <tr key={inst.id} className={`border-b border-border dark:border-gray-700/50 ${inst.status === 'overdue' || (inst.status === 'pending' && new Date(inst.due_date ?? '') < new Date()) ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                            <td className="py-2 px-3 text-foreground dark:text-white text-sm">#{inst.installment_number}{inst.is_entry_fee ? ' (Fee)' : ''}</td>
                            <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm font-mono">{inst.contract_number || '-'}</td>
                            <td className="py-2 px-3 text-right text-foreground dark:text-white font-medium text-sm">€ {Number(inst.amount || 0).toFixed(2)}</td>
                            <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">{new Date(inst.due_date || '').toLocaleDateString('it-IT')}</td>
                            <td className="py-2 px-3">
                              <Badge className={inst.status === 'paid' ? 'bg-green-500/20 text-green-400' : inst.status === 'overdue' ? 'bg-red-500/20 text-red-400' : inst.status === 'failed' ? 'bg-red-500/20 text-red-400' : inst.status === 'processing' ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}>
                                {instStatusLabel[inst.status ?? ''] || inst.status}
                              </Badge>
                              {(legacyNum(inst.retry_count) ?? 0) > 0 && <span className="text-muted-foreground/70 text-xs ml-1">({legacyNum(inst.retry_count)}x)</span>}
                            </td>
                            <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60 text-sm">{inst.paid_at ? new Date(inst.paid_at).toLocaleDateString('it-IT') : '-'}</td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-1">
                                {inst.fic_document_id && (
                                  <Tip label="PDF Fattura"><a href={`/api${CRM_BASE}/fic/documents/${inst.fic_document_id}/pdf`} target="_blank" rel="noreferrer"
                                    className="text-green-400 hover:text-green-300 text-xs font-mono">PDF</a></Tip>
                                )}
                                {(inst.status === 'pending' || inst.status === 'overdue' || inst.status === 'failed') && new Date(inst.due_date ?? '') <= new Date() && !inst.stripe_invoice_id && (
                                  <button onClick={() => chargeInstallmentManually(inst.id)} disabled={chargingInstallment === inst.id}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded disabled:opacity-50">
                                    {chargingInstallment === inst.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
                                    Addebita
                                  </button>
                                )}
                                {(inst.status === 'pending' || inst.status === 'overdue' || inst.status === 'failed') && (
                                  <button onClick={() => setManualPayModal({ id: inst.id, number: inst.installment_number ?? 0, amount: Number(inst.amount) })}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded">
                                    <DollarSign className="h-3 w-3" />
                                    Manuale
                                  </button>
                                )}
                                {inst.stripe_invoice_id && (
                                  <Tip label={`Stripe Invoice: ${inst.stripe_invoice_id}`}>
                                    <span className="text-muted-foreground/70 text-xs font-mono">{inst.stripe_invoice_id.slice(0, 12)}...</span>
                                  </Tip>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}

            {detailTab === 'edit' && editClient && (
              <div className="space-y-4">
                <h4 className="text-foreground dark:text-white font-medium">Dati Azienda</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Ragione Sociale" value={editClient.company_name} onChange={updateClient('company_name')} className="md:col-span-2" uppercase />
                  <SelectField label="Forma Giuridica" value={editClient.legal_form || ''} onChange={updateClient('legal_form')}
                    options={[{ value: '', label: 'Seleziona...' }, ...legalForms.map((lf) => ({ value: lf.code, label: lf.label }))]} />
                  <InputField label="Partita IVA" value={editClient.vat_number} onChange={updateClient('vat_number')} />
                  <InputField label="Codice Fiscale" value={editClient.fiscal_code} onChange={updateClient('fiscal_code')} />
                  <InputField label="Codice SDI" value={editClient.sdi_code} onChange={updateClient('sdi_code')} />
                  <InputField label="PEC" value={editClient.pec} onChange={updateClient('pec')} />
                  <InputField label="Settore" value={editClient.sector} onChange={updateClient('sector')} />
                  <SelectField label="Stato Pipeline" value={legacyStr(editClient.stage_slug)} onChange={updateClient('stage_slug')}
                    options={[{ value: '', label: 'Seleziona...' }, ...Object.entries(pipelineLabels).map(([k, v]) => ({ value: k, label: v }))]} />
                </div>
                <h4 className="text-foreground dark:text-white font-medium mt-4">Sede Legale</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <AddressField value={editClient.legal_address ?? undefined} className="md:col-span-2" onSelect={(a) => setEditClient((c) => ({ ...(c as MasterClientBase), legal_address: a.address ?? '', ...(a.city !== undefined ? { legal_city: a.city } : {}), ...(a.province !== undefined ? { legal_province: a.province } : {}), ...(a.postal_code !== undefined ? { legal_zip: a.postal_code } : {}) }))} />
                  <InputField label="Indirizzo" value={editClient.legal_address} onChange={updateClient('legal_address')} className="md:col-span-2" />
                  <InputField label="Città" value={editClient.legal_city} onChange={updateClient('legal_city')} />
                  <InputField label="Provincia" value={editClient.legal_province} onChange={updateClient('legal_province')} />
                  <InputField label="CAP" value={editClient.legal_zip} onChange={updateClient('legal_zip')} />
                  <InputField label="Paese" value={editClient.legal_country} onChange={updateClient('legal_country')} />
                </div>
                <h4 className="text-foreground dark:text-white font-medium mt-4">Referente</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="Nome" value={editClient.contact_first_name} onChange={updateClient('contact_first_name')} uppercase />
                  <InputField label="Cognome" value={editClient.contact_last_name} onChange={updateClient('contact_last_name')} uppercase />
                  <InputField label="Telefono" value={editClient.contact_phone} onChange={updateClient('contact_phone')} />
                  <InputField label="Email" value={editClient.contact_email} onChange={updateClient('contact_email')} />
                </div>
                <h4 className="text-foreground dark:text-white font-medium mt-4">Dati Bancari</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="IBAN" value={editClient.iban} onChange={updateClient('iban')} />
                  <InputField label="Banca" value={editClient.bank_name} onChange={updateClient('bank_name')} />
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Note</label>
                  <textarea value={editClient.client_notes || ''} onChange={(e) => setEditClient((c) => ({ ...(c as MasterClient), client_notes: e.target.value }))}
                    className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-20 resize-none" />
                </div>
                <h4 className="text-foreground dark:text-white font-medium mt-4 flex items-center gap-2"><DollarSign className="h-4 w-4" /> Listini Prezzi Assegnati</h4>
                <div className="space-y-2">
                  {clientPriceLists.length > 0 ? (
                    <div className="space-y-1">
                      {clientPriceLists.map(cpl => (
                        <div key={cpl.id} className="flex items-center justify-between bg-muted/50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground dark:text-white">{cpl.price_list_name}</span>
                            {cpl.is_default && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Predefinito</Badge>}
                          </div>
                          <button onClick={() => handleRemovePl(cpl.price_list_id)} className="text-muted-foreground/70 hover:text-red-400"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Nessun listino assegnato (verrà usato il listino predefinito)</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <select value={assignPlId} onChange={(e) => setAssignPlId(e.target.value)}
                      className="flex-1 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                      <option value="">Seleziona listino...</option>
                      {allPriceLists.filter(pl => pl.is_active && !clientPriceLists.some((cpl) => cpl.price_list_id === pl.id)).map(pl => (
                        <option key={pl.id} value={pl.id}>{pl.name}{pl.is_default ? ' (predefinito)' : ''}</option>
                      ))}
                    </select>
                    <button onClick={handleAssignPl} disabled={!assignPlId}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                  <button onClick={() => setDetailTab('overview')} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white transition-colors">Annulla</button>
                  <button onClick={handleSaveClient} disabled={saving}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50">
                    <Save className="h-4 w-4" />{saving ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!deleteModal} onClose={() => { if (!deleteLoading) { setDeleteModal(null); setDeleteConfirmText(''); } }} title="Elimina Cliente">
        {deleteModal && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">
              Stai per eliminare <span className="font-semibold text-foreground dark:text-white">{deleteModal.name}</span>. Scegli la modalità di eliminazione.
            </p>
            <div className="space-y-2">
              <label className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteMode === 'client_only' ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10' : 'border-border dark:border-gray-700 hover:bg-muted dark:hover:bg-gray-700/40'}`}>
                <input type="radio" name="deleteMode" className="mt-1" checked={deleteMode === 'client_only'} onChange={() => setDeleteMode('client_only')} />
                <div>
                  <div className="text-sm font-medium text-foreground dark:text-white">Solo anagrafica cliente</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Elimina il cliente e i suoi referenti. Le voci di pipeline collegate vengono scollegate ma conservate.</div>
                </div>
              </label>
              <label className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteMode === 'cascade_safe' ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10' : 'border-border dark:border-gray-700 hover:bg-muted dark:hover:bg-gray-700/40'}`}>
                <input type="radio" name="deleteMode" className="mt-1" checked={deleteMode === 'cascade_safe'} onChange={() => setDeleteMode('cascade_safe')} />
                <div>
                  <div className="text-sm font-medium text-foreground dark:text-white">Cliente + pipeline (sicuro)</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Elimina le voci di pipeline senza dati fiscali. Le voci con fatture o pagamenti vengono conservate e scollegate.</div>
                </div>
              </label>
              <label className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteMode === 'cascade_surgical' ? 'border-red-400 bg-red-50/50 dark:bg-red-900/10' : 'border-border dark:border-gray-700 hover:bg-muted dark:hover:bg-gray-700/40'}`}>
                <input type="radio" name="deleteMode" className="mt-1" checked={deleteMode === 'cascade_surgical'} onChange={() => setDeleteMode('cascade_surgical')} />
                <div>
                  <div className="text-sm font-medium text-red-600 dark:text-red-400">Cliente + pipeline (chirurgico)</div>
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">Come sopra, e dentro le voci fiscali elimina anche rate future non pagate e fatture in bozza. Fatture emesse e pagamenti restano intatti.</div>
                </div>
              </label>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Digita <span className="font-mono font-semibold text-foreground dark:text-white">ELIMINA</span> per confermare</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="ELIMINA"
                className="w-full px-3 py-2 rounded-lg border border-border dark:border-gray-700 bg-white dark:bg-gray-900 text-foreground dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-400"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }} disabled={deleteLoading}
                className="px-4 py-2 rounded-lg border border-border dark:border-gray-700 text-sm text-foreground dark:text-white hover:bg-muted dark:hover:bg-gray-700/50 disabled:opacity-50">
                Annulla
              </button>
              <button onClick={() => void handleDeleteClient()} disabled={deleteLoading || deleteConfirmText.trim().toUpperCase() !== 'ELIMINA'}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {deleteLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Elimina
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>
    </div>
  );
}

